import { useCallback, useEffect, useRef, useState } from "react";

interface FacebookAuthResponse {
  accessToken: string;
  userID: string;
  [key: string]: unknown;
}

interface FacebookLoginStatusResponse {
  status: "connected" | "not_authorized" | "unknown";
  authResponse: FacebookAuthResponse | null;
}

interface FacebookSDK {
  init: (config: {
    appId: string;
    cookie?: boolean;
    xfbml?: boolean;
    version: string;
  }) => void;
  login: (
    callback: (response: FacebookLoginStatusResponse) => void,
    options?: { scope?: string }
  ) => void;
}

declare global {
  interface Window {
    FB?: FacebookSDK;
  }
}

// Bumping this requires checking it's still a supported Graph API version:
// https://developers.facebook.com/docs/graph-api/changelog
const FACEBOOK_SDK_VERSION = "v21.0";
const FACEBOOK_LOGIN_SCOPE = "email,public_profile";

/**
 * Wraps the Facebook JS SDK so Login/Signup only need to call
 * `triggerFacebookSignIn()` and handle the resulting access token via
 * `onToken` - same shape as `useGoogleSignIn`. The SDK script itself is
 * loaded in index.html (https://connect.facebook.net/en_US/sdk.js); this
 * hook waits for it, calls `FB.init()` once, and drives `FB.login()`.
 *
 * Requires VITE_FACEBOOK_APP_ID to be set (non-blank) - if it isn't, `isReady` stays
 * false so callers can hide/disable the Facebook button instead of throwing.
 */
export const useFacebookSignIn = (onToken: (accessToken: string) => void) => {
  // Trimmed so a blank value (e.g. " " in the Amplify console) counts as unset.
  const appId =
    (import.meta.env.VITE_FACEBOOK_APP_ID as string | undefined)?.trim();
  const [isReady, setIsReady] = useState(false);
  const initializedRef = useRef(false);

  // Keep the latest callback without re-running the setup effect below.
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    if (!appId) {
      console.warn(
        "VITE_FACEBOOK_APP_ID is not set - Facebook sign-in is disabled."
      );
      return;
    }

    let cancelled = false;
    let pollId: ReturnType<typeof setInterval> | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const setup = () => {
      if (cancelled || !window.FB) return;
      // FB.init() is meant to be called once per page load; guard against
      // re-running it if this effect re-fires (e.g. React StrictMode).
      if (!initializedRef.current) {
        window.FB.init({
          appId,
          cookie: false,
          xfbml: false,
          version: FACEBOOK_SDK_VERSION,
        });
        initializedRef.current = true;
      }
      setIsReady(true);
    };

    if (window.FB) {
      setup();
    } else {
      // The Facebook SDK script tag is async; poll briefly until it's loaded.
      pollId = setInterval(() => {
        if (window.FB) {
          clearInterval(pollId);
          setup();
        }
      }, 100);
      timeoutId = setTimeout(() => clearInterval(pollId), 10000);
    }

    return () => {
      cancelled = true;
      clearInterval(pollId);
      clearTimeout(timeoutId);
    };
  }, [appId]);

  const triggerFacebookSignIn = useCallback(() => {
    window.FB?.login(
      (response) => {
        if (response.status === "connected" && response.authResponse) {
          onTokenRef.current(response.authResponse.accessToken);
        }
        // "not_authorized" / "unknown": user closed the dialog or declined -
        // no token to hand off, nothing to report as an error either.
      },
      { scope: FACEBOOK_LOGIN_SCOPE }
    );
  }, []);

  return { triggerFacebookSignIn, isReady };
};
